import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, Plus, Trash2, Pencil, X, Users, BookOpen, GraduationCap,
  Filter, BadgeDollarSign, Sparkles, FileText, BarChart3, CalendarRange,
} from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_LABELS = {
  pendiente: "Pendiente",
  impartida: "Completada",
  no_show_student: "No asistió alumno",
  no_show_teacher: "No asistió profesora",
  reprogramada: "Reprogramada",
};

const STATUS_OPTIONS = [
  { value: "pendiente", label: "Pendiente" },
  { value: "impartida", label: "Completada" },
  { value: "no_show_student", label: "No asistió alumno" },
  { value: "no_show_teacher", label: "No asistió profesora" },
  { value: "reprogramada", label: "Reprogramada" },
];

const formatStamp = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const emptyClass = {
  teacher_id: "", date: "", time: "", student_name: "", student_id: "",
  class_type: "", category: "paid", link: "", notas: "", status: "pendiente",
};
const emptyTeacher = { name: "", email: "", password: "" };
const emptyStudent = { name: "", total_classes_purchased: 0 };
const emptyEnrol = { student_id: "", teacher_id: "", class_type: "", start_date: "", time: "", num_classes: 8, link: "", notas: "" };
const emptyFilters = { teacher_id: "", student_name: "", category: "", class_type: "", status: "" };

const AdminPanel = () => {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("classes");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [filters, setFilters] = useState(emptyFilters);

  const [showClassForm, setShowClassForm] = useState(false);
  const [classForm, setClassForm] = useState(emptyClass);
  const [editingClassId, setEditingClassId] = useState(null);

  const [showTeacherForm, setShowTeacherForm] = useState(false);
  const [teacherForm, setTeacherForm] = useState(emptyTeacher);

  const [showStudentForm, setShowStudentForm] = useState(false);
  const [studentForm, setStudentForm] = useState(emptyStudent);
  const [editingStudentId, setEditingStudentId] = useState(null);

  const [showEnrolForm, setShowEnrolForm] = useState(false);
  const [enrolForm, setEnrolForm] = useState(emptyEnrol);

  const [viewNotesFor, setViewNotesFor] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [c, t, s, p] = await Promise.all([
        api.get("/admin/classes"),
        api.get("/admin/teachers"),
        api.get("/admin/students"),
        api.get("/admin/performance"),
      ]);
      setClasses(c.data); setTeachers(t.data); setStudents(s.data); setPerformance(p.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const filteredClasses = useMemo(() => classes.filter((c) => {
    if (filters.teacher_id && c.teacher_id !== filters.teacher_id) return false;
    if (filters.category && c.category !== filters.category) return false;
    if (filters.status && c.status !== filters.status) return false;
    if (filters.class_type && c.class_type.toLowerCase() !== filters.class_type.toLowerCase()) return false;
    if (filters.student_name) {
      const q = filters.student_name.toLowerCase();
      if (!c.student_name.toLowerCase().includes(q)) return false;
    }
    return true;
  }), [classes, filters]);

  const distinctClassTypes = useMemo(() => {
    const set = new Set(classes.map((c) => c.class_type).filter(Boolean));
    return Array.from(set).sort();
  }, [classes]);

  // ---- Class form ----
  const openCreateClass = () => {
    setClassForm({ ...emptyClass, teacher_id: teachers[0]?.id || "" });
    setEditingClassId(null);
    setShowClassForm(true);
  };

  const openEditClass = (c) => {
    setClassForm({
      teacher_id: c.teacher_id, date: c.date, time: c.time,
      student_name: c.student_name, student_id: c.student_id || "",
      class_type: c.class_type, category: c.category || "paid",
      link: c.link || "", notas: c.notas || "", status: c.status,
    });
    setEditingClassId(c.id);
    setShowClassForm(true);
  };

  const submitClass = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...classForm };
      if (payload.category === "trial") {
        payload.student_id = null;
      } else if (payload.student_id) {
        const stu = students.find((s) => s.id === payload.student_id);
        if (stu) payload.student_name = stu.name;
      } else {
        payload.student_id = null;
      }
      if (editingClassId) {
        await api.put(`/admin/classes/${editingClassId}`, payload);
      } else {
        await api.post("/admin/classes", payload);
      }
      setShowClassForm(false);
      setEditingClassId(null);
      setClassForm(emptyClass);
      loadAll();
    } catch (err) {
      alert(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteClass = async (id) => {
    if (!window.confirm("¿Eliminar esta clase?")) return;
    try {
      await api.delete(`/admin/classes/${id}`);
      loadAll();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  // ---- Teacher ----
  const submitTeacher = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/teachers", teacherForm);
      setShowTeacherForm(false); setTeacherForm(emptyTeacher); loadAll();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const deleteTeacher = async (id) => {
    if (!window.confirm("¿Eliminar esta profesora? Sus clases también se eliminarán.")) return;
    try { await api.delete(`/admin/teachers/${id}`); loadAll(); }
    catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  // ---- Student ----
  const openCreateStudent = () => { setStudentForm(emptyStudent); setEditingStudentId(null); setShowStudentForm(true); };
  const openEditStudent = (s) => {
    setStudentForm({ name: s.name, total_classes_purchased: s.total_classes_purchased });
    setEditingStudentId(s.id); setShowStudentForm(true);
  };
  const submitStudent = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...studentForm, total_classes_purchased: parseInt(studentForm.total_classes_purchased, 10) || 0 };
      if (editingStudentId) await api.put(`/admin/students/${editingStudentId}`, payload);
      else await api.post("/admin/students", payload);
      setShowStudentForm(false); setEditingStudentId(null); setStudentForm(emptyStudent); loadAll();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const deleteStudent = async (id) => {
    if (!window.confirm("¿Eliminar este alumno? Las clases asociadas mantendrán el nombre pero perderán el vínculo.")) return;
    try { await api.delete(`/admin/students/${id}`); loadAll(); }
    catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  // ---- Enrollment ----
  const openEnrolFor = (student) => {
    const remaining = student.classes_remaining || student.total_classes_purchased || 8;
    setEnrolForm({
      ...emptyEnrol,
      student_id: student.id,
      num_classes: remaining > 0 ? remaining : 8,
      teacher_id: teachers[0]?.id || "",
    });
    setShowEnrolForm(true);
  };
  const submitEnrol = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...enrolForm, num_classes: parseInt(enrolForm.num_classes, 10) || 1 };
      const { data } = await api.post("/admin/enrollments", payload);
      setShowEnrolForm(false); setEnrolForm(emptyEnrol);
      alert(`Se generaron ${data.created} clases.`);
      loadAll();
    } catch (err) { alert(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const resetFilters = () => setFilters(emptyFilters);
  const filtersActive = Object.values(filters).some(Boolean);

  return (
    <div className="dash-shell" data-testid="admin-panel">
      <header className="dash-header">
        <div className="dash-header-inner">
          <Link to="/" className="dash-brand">
            <span className="dash-brand-text">Laboratorio Secreto</span>
            <span className="dash-brand-sub">Panel de administración</span>
          </Link>
          <div className="dash-user">
            <div className="dash-user-meta">
              <span className="dash-user-name">{user?.name}</span>
              <span className="dash-user-email">{user?.email}</span>
            </div>
            <button onClick={logout} className="dash-logout" data-testid="admin-logout">
              <LogOut size={16} /><span>Cerrar sesión</span>
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <div className="dash-container">
          <div className="dash-tabs">
            <button className={`dash-tab ${tab === "classes" ? "is-active" : ""}`} onClick={() => setTab("classes")} data-testid="tab-classes">
              <BookOpen size={16} /> Clases<span className="dash-tab-count">{classes.length}</span>
            </button>
            <button className={`dash-tab ${tab === "students" ? "is-active" : ""}`} onClick={() => setTab("students")} data-testid="tab-students">
              <GraduationCap size={16} /> Alumnos<span className="dash-tab-count">{students.length}</span>
            </button>
            <button className={`dash-tab ${tab === "teachers" ? "is-active" : ""}`} onClick={() => setTab("teachers")} data-testid="tab-teachers">
              <Users size={16} /> Profesoras<span className="dash-tab-count">{teachers.length}</span>
            </button>
            <button className={`dash-tab ${tab === "performance" ? "is-active" : ""}`} onClick={() => setTab("performance")} data-testid="tab-performance">
              <BarChart3 size={16} /> Desempeño
            </button>
          </div>

          {loading && <div className="dash-empty">Cargando…</div>}
          {error && <div className="dash-empty dash-error">{error}</div>}

          {!loading && tab === "classes" && (
            <>
              <div className="filters-bar" data-testid="admin-filters">
                <div className="filters-head"><Filter size={14} /> <span>Filtros</span></div>
                <select value={filters.teacher_id} onChange={(e) => setFilters({ ...filters, teacher_id: e.target.value })} className="auth-input filter-input" data-testid="filter-teacher">
                  <option value="">Todas las profesoras</option>
                  {teachers.map((t) => (<option key={t.id} value={t.id}>{t.name}</option>))}
                </select>
                <input type="text" placeholder="Buscar alumno…" value={filters.student_name} onChange={(e) => setFilters({ ...filters, student_name: e.target.value })} className="auth-input filter-input" data-testid="filter-student" />
                <select value={filters.category} onChange={(e) => setFilters({ ...filters, category: e.target.value })} className="auth-input filter-input" data-testid="filter-category">
                  <option value="">Todas las categorías</option>
                  <option value="trial">Clase de prueba</option>
                  <option value="paid">Clase pagada</option>
                </select>
                <select value={filters.class_type} onChange={(e) => setFilters({ ...filters, class_type: e.target.value })} className="auth-input filter-input" data-testid="filter-class-type">
                  <option value="">Todos los tipos</option>
                  {distinctClassTypes.map((ct) => (<option key={ct} value={ct}>{ct}</option>))}
                </select>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="auth-input filter-input" data-testid="filter-status">
                  <option value="">Todos los estados</option>
                  {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
                {filtersActive && (
                  <button type="button" className="filter-reset" onClick={resetFilters} data-testid="filter-reset">
                    <X size={12} /> Limpiar
                  </button>
                )}
                <div className="filter-spacer" />
                <button onClick={openCreateClass} className="dash-mark-btn" disabled={teachers.length === 0} data-testid="open-create-class">
                  <Plus size={16} /> Nueva clase
                </button>
              </div>

              {filteredClasses.length === 0 ? (
                <div className="dash-empty">{classes.length === 0 ? "No hay clases registradas." : "Sin resultados con los filtros aplicados."}</div>
              ) : (
                <div className="admin-table-wrap" data-testid="admin-classes-table">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Fecha</th><th>Hora</th><th>Alumno</th><th>Tipo</th><th>Categoría</th>
                        <th>Profesora</th><th>Estado</th><th>Comentarios</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClasses.map((c) => (
                        <tr key={c.id} data-testid={`admin-class-row-${c.id}`}>
                          <td>{c.date}</td>
                          <td>{c.time}</td>
                          <td>{c.student_name}</td>
                          <td>{c.class_type}</td>
                          <td>
                            <span className={`dash-cat dash-cat-${c.category || "paid"}`}>
                              {c.category === "trial" ? (<><Sparkles size={12} /> Prueba</>) : (<><BadgeDollarSign size={12} /> Pagada</>)}
                            </span>
                          </td>
                          <td>{c.teacher_name || "—"}</td>
                          <td>
                            <span className={`dash-status dash-status-${c.status}`} data-testid={`admin-status-${c.id}`}>
                              {STATUS_LABELS[c.status] || c.status}
                            </span>
                          </td>
                          <td className="admin-notes-cell">
                            {c.teacher_notes ? (
                              <button type="button" className="notes-link" onClick={() => setViewNotesFor(c)} data-testid={`view-notes-${c.id}`}>
                                <FileText size={12} /> Ver
                              </button>
                            ) : (<span className="notes-empty">—</span>)}
                          </td>
                          <td className="admin-actions">
                            <button className="icon-btn" onClick={() => openEditClass(c)} data-testid={`edit-class-${c.id}`} aria-label="Editar"><Pencil size={14} /></button>
                            <button className="icon-btn icon-btn-danger" onClick={() => deleteClass(c.id)} data-testid={`delete-class-${c.id}`} aria-label="Eliminar"><Trash2 size={14} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!loading && tab === "students" && (
            <>
              <div className="dash-actions">
                <button onClick={openCreateStudent} className="dash-mark-btn" data-testid="open-create-student">
                  <Plus size={16} /> Nuevo alumno
                </button>
              </div>

              {students.length === 0 ? (
                <div className="dash-empty">
                  Aún no has registrado alumnos. Los alumnos aquí son los que tienen clases pagadas.
                </div>
              ) : (
                <div className="admin-table-wrap" data-testid="admin-students-table">
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Alumno</th><th>Compradas</th><th>Completadas</th><th>Restantes</th>
                        <th>Progreso</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {students.map((s) => {
                        const pct = s.total_classes_purchased
                          ? Math.min(100, Math.round((s.classes_completed / s.total_classes_purchased) * 100))
                          : 0;
                        return (
                          <tr key={s.id} data-testid={`admin-student-row-${s.id}`}>
                            <td>{s.name}</td>
                            <td>{s.total_classes_purchased}</td>
                            <td>{s.classes_completed}</td>
                            <td>
                              <strong className={s.classes_remaining <= 1 ? "muted-pink" : ""}>{s.classes_remaining}</strong>
                            </td>
                            <td>
                              <div className="progress-bar">
                                <div className="progress-fill" style={{ width: `${pct}%` }} data-testid={`student-progress-${s.id}`} />
                                <span className="progress-text">{pct}%</span>
                              </div>
                            </td>
                            <td className="admin-actions">
                              <button className="icon-btn" onClick={() => openEnrolFor(s)} data-testid={`enrol-student-${s.id}`} aria-label="Generar clases recurrentes" title="Generar clases recurrentes">
                                <CalendarRange size={14} />
                              </button>
                              <button className="icon-btn" onClick={() => openEditStudent(s)} data-testid={`edit-student-${s.id}`} aria-label="Editar"><Pencil size={14} /></button>
                              <button className="icon-btn icon-btn-danger" onClick={() => deleteStudent(s.id)} data-testid={`delete-student-${s.id}`} aria-label="Eliminar"><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!loading && tab === "teachers" && (
            <>
              <div className="dash-actions">
                <button onClick={() => { setTeacherForm(emptyTeacher); setShowTeacherForm(true); }} className="dash-mark-btn" data-testid="open-create-teacher">
                  <Plus size={16} /> Nueva profesora
                </button>
              </div>
              {teachers.length === 0 ? (
                <div className="dash-empty">No hay profesoras registradas.</div>
              ) : (
                <div className="admin-table-wrap" data-testid="admin-teachers-table">
                  <table className="admin-table">
                    <thead><tr><th>Nombre</th><th>Email</th><th>Clases asignadas</th><th></th></tr></thead>
                    <tbody>
                      {teachers.map((t) => {
                        const count = classes.filter((c) => c.teacher_id === t.id).length;
                        return (
                          <tr key={t.id} data-testid={`admin-teacher-row-${t.id}`}>
                            <td>{t.name}</td><td>{t.email}</td><td>{count}</td>
                            <td className="admin-actions">
                              <button className="icon-btn icon-btn-danger" onClick={() => deleteTeacher(t.id)} data-testid={`delete-teacher-${t.id}`} aria-label="Eliminar"><Trash2 size={14} /></button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {!loading && tab === "performance" && (
            <div data-testid="admin-performance-table">
              <p className="perf-intro">
                Resumen de actividad por profesora. Los porcentajes de conversión muestran cuántos alumnos
                de prueba terminaron tomando clases pagadas.
              </p>
              {performance.length === 0 ? (
                <div className="dash-empty">No hay profesoras registradas.</div>
              ) : (
                <div className="admin-table-wrap">
                  <table className="admin-table perf-table">
                    <thead>
                      <tr>
                        <th>Profesora</th><th>Total</th><th>Completadas</th><th>Pendientes</th>
                        <th>No-show alumno</th><th>No-show profesora</th><th>Reprogramadas</th>
                        <th>Trial / Pagadas</th><th>Conversión trial→pagada</th>
                      </tr>
                    </thead>
                    <tbody>
                      {performance.map((p) => (
                        <tr key={p.teacher_id} data-testid={`perf-row-${p.teacher_id}`}>
                          <td><strong>{p.teacher_name}</strong><div className="perf-email">{p.teacher_email}</div></td>
                          <td>{p.total_classes}</td>
                          <td><span className="dash-status dash-status-impartida">{p.completed_classes}</span></td>
                          <td>{p.pending_classes}</td>
                          <td>{p.no_show_student}</td>
                          <td>{p.no_show_teacher}</td>
                          <td>{p.rescheduled_classes}</td>
                          <td>{p.trial_classes} / {p.paid_classes}</td>
                          <td data-testid={`perf-conv-${p.teacher_id}`}>
                            <strong>{Math.round(p.trial_to_paid_rate * 100)}%</strong>
                            <div className="perf-sub">{p.trial_converted} / {p.trial_students} alumnos</div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Class form modal */}
      {showClassForm && (
        <div className="modal-backdrop" onClick={() => setShowClassForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="class-form-modal">
            <button className="modal-close" onClick={() => setShowClassForm(false)} aria-label="Cerrar"><X size={18} /></button>
            <h3 className="modal-title">{editingClassId ? "Editar clase" : "Nueva clase"}</h3>
            <form onSubmit={submitClass} className="modal-form">
              <div className="modal-grid">
                <label className="auth-field">
                  <span className="auth-label">Categoría</span>
                  <select required value={classForm.category}
                    onChange={(e) => {
                      const cat = e.target.value;
                      setClassForm((f) => ({ ...f, category: cat, student_id: cat === "trial" ? "" : f.student_id }));
                    }}
                    className="auth-input" data-testid="class-form-category">
                    <option value="paid">Clase pagada</option>
                    <option value="trial">Clase de prueba</option>
                  </select>
                </label>
                <label className="auth-field">
                  <span className="auth-label">Profesora</span>
                  <select required value={classForm.teacher_id} onChange={(e) => setClassForm({ ...classForm, teacher_id: e.target.value })} className="auth-input" data-testid="class-form-teacher">
                    <option value="">Selecciona…</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </label>
                <label className="auth-field">
                  <span className="auth-label">Tipo</span>
                  <input type="text" required placeholder="Scratch, Python, Robótica…" value={classForm.class_type} onChange={(e) => setClassForm({ ...classForm, class_type: e.target.value })} className="auth-input" data-testid="class-form-type" />
                </label>
                <label className="auth-field">
                  <span className="auth-label">Fecha</span>
                  <input type="date" required value={classForm.date} onChange={(e) => setClassForm({ ...classForm, date: e.target.value })} className="auth-input" data-testid="class-form-date" />
                </label>
                <label className="auth-field">
                  <span className="auth-label">Hora</span>
                  <input type="time" required value={classForm.time} onChange={(e) => setClassForm({ ...classForm, time: e.target.value })} className="auth-input" data-testid="class-form-time" />
                </label>

                {classForm.category === "paid" ? (
                  <label className="auth-field auth-field-full">
                    <span className="auth-label">Alumno (de la lista)</span>
                    <select value={classForm.student_id}
                      onChange={(e) => {
                        const id = e.target.value;
                        const stu = students.find((s) => s.id === id);
                        setClassForm({ ...classForm, student_id: id, student_name: stu ? stu.name : classForm.student_name });
                      }}
                      className="auth-input" data-testid="class-form-student-id">
                      <option value="">Selecciona alumno…</option>
                      {students.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.classes_remaining} restantes)</option>)}
                    </select>
                    <small className="field-hint">Si el alumno no está, créalo en la pestaña «Alumnos».</small>
                  </label>
                ) : (
                  <label className="auth-field auth-field-full">
                    <span className="auth-label">Nombre del alumno (clase de prueba)</span>
                    <input type="text" required value={classForm.student_name} onChange={(e) => setClassForm({ ...classForm, student_name: e.target.value })} className="auth-input" data-testid="class-form-student" />
                  </label>
                )}

                <label className="auth-field auth-field-full">
                  <span className="auth-label">Link de la clase (opcional)</span>
                  <input type="url" placeholder="https://meet.google.com/…" value={classForm.link} onChange={(e) => setClassForm({ ...classForm, link: e.target.value })} className="auth-input" data-testid="class-form-link" />
                </label>
                <label className="auth-field auth-field-full">
                  <span className="auth-label">Notas internas (opcional)</span>
                  <textarea rows="3" value={classForm.notas} onChange={(e) => setClassForm({ ...classForm, notas: e.target.value })} className="auth-input" data-testid="class-form-notas" />
                </label>

                {editingClassId && (
                  <label className="auth-field auth-field-full">
                    <span className="auth-label">Estado</span>
                    <select value={classForm.status} onChange={(e) => setClassForm({ ...classForm, status: e.target.value })} className="auth-input" data-testid="class-form-status">
                      {STATUS_OPTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                    <small className="field-hint">«No asistió profesora» solo puede asignarlo el administrador.</small>
                  </label>
                )}
              </div>
              <button type="submit" className="auth-submit" data-testid="class-form-submit">
                {editingClassId ? "Guardar cambios" : "Crear clase"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Teacher form */}
      {showTeacherForm && (
        <div className="modal-backdrop" onClick={() => setShowTeacherForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="teacher-form-modal">
            <button className="modal-close" onClick={() => setShowTeacherForm(false)} aria-label="Cerrar"><X size={18} /></button>
            <h3 className="modal-title">Nueva profesora</h3>
            <form onSubmit={submitTeacher} className="modal-form">
              <label className="auth-field"><span className="auth-label">Nombre</span>
                <input type="text" required value={teacherForm.name} onChange={(e) => setTeacherForm({ ...teacherForm, name: e.target.value })} className="auth-input" data-testid="teacher-form-name" /></label>
              <label className="auth-field"><span className="auth-label">Email</span>
                <input type="email" required value={teacherForm.email} onChange={(e) => setTeacherForm({ ...teacherForm, email: e.target.value })} className="auth-input" data-testid="teacher-form-email" /></label>
              <label className="auth-field"><span className="auth-label">Contraseña (mín. 6)</span>
                <input type="text" required minLength={6} value={teacherForm.password} onChange={(e) => setTeacherForm({ ...teacherForm, password: e.target.value })} className="auth-input" data-testid="teacher-form-password" /></label>
              <button type="submit" className="auth-submit" data-testid="teacher-form-submit">Crear profesora</button>
            </form>
          </div>
        </div>
      )}

      {/* Student form */}
      {showStudentForm && (
        <div className="modal-backdrop" onClick={() => setShowStudentForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="student-form-modal">
            <button className="modal-close" onClick={() => setShowStudentForm(false)} aria-label="Cerrar"><X size={18} /></button>
            <h3 className="modal-title">{editingStudentId ? "Editar alumno" : "Nuevo alumno"}</h3>
            <form onSubmit={submitStudent} className="modal-form">
              <label className="auth-field"><span className="auth-label">Nombre del alumno</span>
                <input type="text" required value={studentForm.name} onChange={(e) => setStudentForm({ ...studentForm, name: e.target.value })} className="auth-input" data-testid="student-form-name" /></label>
              <label className="auth-field"><span className="auth-label">Total de clases pagadas</span>
                <input type="number" required min={0} value={studentForm.total_classes_purchased} onChange={(e) => setStudentForm({ ...studentForm, total_classes_purchased: e.target.value })} className="auth-input" data-testid="student-form-total" />
                <small className="field-hint">Las clases completadas se cuentan automáticamente al marcarlas como impartidas.</small></label>
              <button type="submit" className="auth-submit" data-testid="student-form-submit">{editingStudentId ? "Guardar cambios" : "Crear alumno"}</button>
            </form>
          </div>
        </div>
      )}

      {/* Enrollment form (recurring schedule) */}
      {showEnrolForm && (
        <div className="modal-backdrop" onClick={() => setShowEnrolForm(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="enrol-form-modal">
            <button className="modal-close" onClick={() => setShowEnrolForm(false)} aria-label="Cerrar"><X size={18} /></button>
            <h3 className="modal-title">Generar clases recurrentes</h3>
            <p className="field-hint" style={{ marginBottom: "1rem" }}>
              Crea N clases pagadas, cada una a la misma hora y con una semana de diferencia, comenzando en la fecha indicada.
            </p>
            <form onSubmit={submitEnrol} className="modal-form">
              <div className="modal-grid">
                <label className="auth-field"><span className="auth-label">Profesora</span>
                  <select required value={enrolForm.teacher_id} onChange={(e) => setEnrolForm({ ...enrolForm, teacher_id: e.target.value })} className="auth-input" data-testid="enrol-form-teacher">
                    <option value="">Selecciona…</option>
                    {teachers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select></label>
                <label className="auth-field"><span className="auth-label">Tipo</span>
                  <input type="text" required placeholder="Scratch, Python…" value={enrolForm.class_type} onChange={(e) => setEnrolForm({ ...enrolForm, class_type: e.target.value })} className="auth-input" data-testid="enrol-form-type" /></label>
                <label className="auth-field"><span className="auth-label">Fecha primera clase</span>
                  <input type="date" required value={enrolForm.start_date} onChange={(e) => setEnrolForm({ ...enrolForm, start_date: e.target.value })} className="auth-input" data-testid="enrol-form-start" /></label>
                <label className="auth-field"><span className="auth-label">Hora</span>
                  <input type="time" required value={enrolForm.time} onChange={(e) => setEnrolForm({ ...enrolForm, time: e.target.value })} className="auth-input" data-testid="enrol-form-time" /></label>
                <label className="auth-field"><span className="auth-label">Número de clases</span>
                  <input type="number" required min={1} max={100} value={enrolForm.num_classes} onChange={(e) => setEnrolForm({ ...enrolForm, num_classes: e.target.value })} className="auth-input" data-testid="enrol-form-num" /></label>
                <label className="auth-field auth-field-full"><span className="auth-label">Link (opcional)</span>
                  <input type="url" value={enrolForm.link} onChange={(e) => setEnrolForm({ ...enrolForm, link: e.target.value })} className="auth-input" data-testid="enrol-form-link" /></label>
                <label className="auth-field auth-field-full"><span className="auth-label">Notas internas (opcional)</span>
                  <textarea rows="2" value={enrolForm.notas} onChange={(e) => setEnrolForm({ ...enrolForm, notas: e.target.value })} className="auth-input" data-testid="enrol-form-notas" /></label>
              </div>
              <button type="submit" className="auth-submit" data-testid="enrol-form-submit">Generar clases</button>
            </form>
          </div>
        </div>
      )}

      {/* View teacher notes */}
      {viewNotesFor && (
        <div className="modal-backdrop" onClick={() => setViewNotesFor(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="view-notes-modal">
            <button className="modal-close" onClick={() => setViewNotesFor(null)} aria-label="Cerrar"><X size={18} /></button>
            <h3 className="modal-title">Comentarios de la profesora</h3>
            <div className="notes-meta">
              <div><strong>Alumno:</strong> {viewNotesFor.student_name}</div>
              <div><strong>Profesora:</strong> {viewNotesFor.teacher_name || "—"}</div>
              <div><strong>Fecha:</strong> {viewNotesFor.date} {viewNotesFor.time}</div>
              <div><strong>Tipo:</strong> {viewNotesFor.class_type}</div>
              <div><strong>Estado:</strong> {STATUS_LABELS[viewNotesFor.status] || viewNotesFor.status}</div>
              {viewNotesFor.notes_updated_at && (
                <div><strong>Actualizado:</strong> {formatStamp(viewNotesFor.notes_updated_at)}</div>
              )}
            </div>
            <div className="notes-body" data-testid="view-notes-body">
              {viewNotesFor.teacher_notes || "—"}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;
